import { getEmailReply, getEmailSummary } from './functions'
import { useHistory } from "react-router-dom/cjs/react-router-dom"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { handleLogout, microsoftAuthService, msalInstance } from './outlook/config'
import { handleGoogleLogin, handleGoogleLogout, initGoogleClient } from "./google/config"
import { deleteEmail, fetchEmails, getMicrosoftAttachmentContent, sendMicrosoftReply } from "./outlook/emails"
import { deleteGmailEmail, fetchGmailEmails, getGmailAttachmentContent, sendGmailReply } from "./google/gmail"
import { dark_mode_primary, dark_mode_secondary, AppMode, oAuthStatus, AuthProvider, PaymentTiers, light_mode_primary, light_mode_secondary } from "./values"
import { AccountSelector, AttachmentErrorBoundary, AttachmentPreviewPopup, DeletePopup, ExplanationPopup, LoadingScreen, NoAuthScreen, ReplyPopup } from './widgets'

export function Emails({ appMode, user, setUser, accounts, setAccounts, error, setError, loading, setLoading, emails, setEmails, isGoogleLoggedIn, isMicrosoftLoggedIn, setIsGoogleLoggedIn, setIsMicrosoftLoggedIn, authManager, providerAccounts, setActiveTab, subscriptionTier }) {
    const history = useHistory();
    const initializedRef = useRef(false);
    const [updateTrigger, setUpdateTrigger] = useState(0);
    const [activeAccount, setActiveAccount] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [fetchingEmails, setFetchingEmails] = useState(false);
    const [subscriptionInfo, setSubscriptionInfo] = useState(null);
    const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
    const [expiredAccountInfo, setExpiredAccountInfo] = useState(null);
    const [showAccountSelector, setShowAccountSelector] = useState(false);
    const [showSessionExpiredAlert, setShowSessionExpiredAlert] = useState(false);

    useEffect(() => {
        if (authManager) {
            setAccounts(authManager.getLoggedInAccounts());
        }//eslint-disable-next-line
    }, [setAccounts]);

    useEffect(() => {
        const handleAuthStateChange = () => {
            if (authManager) {
                setAccounts(authManager.getLoggedInAccounts());
                setUpdateTrigger(prev => prev + 1);
            }
        };

        window.addEventListener('authStateChanged', handleAuthStateChange);
        authManager.addStateChangeListener(handleAuthStateChange);
        return () => {
            window.removeEventListener('authStateChanged', handleAuthStateChange);
            authManager.removeStateChangeListener(handleAuthStateChange);
        };//eslint-disable-next-line
    }, [setAccounts]);

    useEffect(() => {
        if (authManager && subscriptionTier !== undefined) {
            try {
                setSubscriptionInfo({
                    tier: subscriptionTier,
                    name: subscriptionTier === 0
                        ? 'Free'
                        : subscriptionTier === 1
                            ? 'Starter'
                            : subscriptionTier === 2
                                ? 'Pro'
                                : 'Enterprise',
                    accountLimit: subscriptionTier === 0
                        ? 2
                        : subscriptionTier === 1
                            ? 5
                            : subscriptionTier === 2
                                ? 10
                                : 15,
                    accountsUsed: authManager.getLoggedInAccounts().length,
                    canAddMore: authManager.canAddMoreAccounts(),
                });
            } catch (error) {
                setSubscriptionInfo({
                    tier: PaymentTiers.FREE,
                    name: 'Free',
                    accountLimit: 2,
                    accountsUsed: 0,
                    canAddMore: true,
                });
            }
        }//eslint-disable-next-line
    }, [subscriptionTier, updateTrigger]);

    const canAddMoreAccounts = useCallback(() => {
        if (!authManager) return true;
        try {
            return authManager.canAddMoreAccounts();
        } catch (error) {
            return false;
        }//eslint-disable-next-line
    }, []);

    const handleUpgradeRedirect = () => {
        setShowUpgradePrompt(false);
        history.push('/pricing');
    };

    useEffect(() => {
        if (!activeAccount && providerAccounts) {
            const googleActive = providerAccounts[AuthProvider.GOOGLE];
            const microsoftActive = providerAccounts[AuthProvider.MICROSOFT];
            if (googleActive) {
                setActiveAccount(googleActive);
            } else if (microsoftActive) {
                setActiveAccount(microsoftActive);
            }
        }
    }, [activeAccount, providerAccounts]);

    useEffect(() => {
        const initializeAuth = async () => {
            if (initializedRef.current) return;
            try {
                setLoading(true);
                initializedRef.current = true;
                if (isMicrosoftLoggedIn === oAuthStatus.LOGGED_IN) {
                    try {
                        await microsoftAuthService.initialize();
                        const result = await msalInstance.handleRedirectPromise();
                        if (result) {
                            const newAccount = {
                                ...result.account,
                                provider: AuthProvider.MICROSOFT,
                                email: result.account.username,
                                username: result.account.username,
                            };
                            setUser(newAccount);
                            if (authManager) {
                                try {
                                    if (!canAddMoreAccounts()) {
                                        setShowUpgradePrompt(true);
                                        return;
                                    }
                                    const accountRecord = authManager.addAccount(AuthProvider.MICROSOFT, newAccount);
                                    if (accountRecord) {
                                        authManager.setActiveAccount(accountRecord.id);
                                        setActiveAccount(newAccount);
                                        if (!fetchingEmails) {
                                            setFetchingEmails(true);
                                            await fetchEmails(result.account, setLoading, setEmails, setError);
                                            setFetchingEmails(false);
                                        }
                                    }
                                } catch (error) {
                                    if (error.message && error.message.includes("Account limit reached")) {
                                        setShowUpgradePrompt(true);
                                        return;
                                    }
                                    throw error;
                                }
                            }
                        } else {
                            const msAccounts = msalInstance.getAllAccounts();
                            if (msAccounts.length > 0) {
                                if (authManager) {
                                    const storedMsAccounts = authManager.getAccounts(AuthProvider.MICROSOFT);
                                    if (storedMsAccounts.length === 0 && msAccounts.length > 0) {
                                        const primaryAccount = {
                                            ...msAccounts[0],
                                            provider: AuthProvider.MICROSOFT,
                                            email: msAccounts[0].username,
                                            username: msAccounts[0].username,
                                        };
                                        setUser(primaryAccount);
                                        try {
                                            if (!canAddMoreAccounts()) {
                                                setShowUpgradePrompt(true);
                                                return;
                                            }
                                            const accountRecord = authManager.addAccount(AuthProvider.MICROSOFT, primaryAccount);
                                            if (accountRecord) {
                                                authManager.setActiveAccount(accountRecord.id);
                                                setActiveAccount(primaryAccount);
                                                if (!fetchingEmails) {
                                                    setFetchingEmails(true);
                                                    await fetchEmails(msAccounts[0], setLoading, setEmails, setError);
                                                    setFetchingEmails(false);
                                                }
                                            }
                                        } catch (error) {
                                            if (error.message && error.message.includes("Account limit reached")) {
                                                setShowUpgradePrompt(true);
                                                return;
                                            }
                                            throw error;
                                        }
                                    } else if (storedMsAccounts.length > 0) {
                                        const activeAccount = authManager.getActiveAccount(AuthProvider.MICROSOFT);
                                        if (activeAccount) {
                                            setUser(activeAccount.userData);
                                            setActiveAccount(activeAccount.userData);
                                            if (!fetchingEmails) {
                                                setFetchingEmails(true);
                                                await fetchEmails(activeAccount.userData, setLoading, setEmails, setError);
                                                setFetchingEmails(false);
                                            }
                                        } else {
                                            setUser(storedMsAccounts[0].userData);
                                            setActiveAccount(storedMsAccounts[0].userData);
                                            authManager.setActiveAccount(storedMsAccounts[0].id);
                                            if (!fetchingEmails) {
                                                setFetchingEmails(true);
                                                await fetchEmails(storedMsAccounts[0].userData, setLoading, setEmails, setError);
                                                setFetchingEmails(false);
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    } catch (error) {
                        if (error.message && (error.message.includes("MSAL account not found") ||
                            error.message.includes("Session expired"))) {
                            setExpiredAccountInfo({
                                provider: AuthProvider.MICROSOFT,
                                accounts: authManager.getAccounts(AuthProvider.MICROSOFT)
                            });
                            setShowSessionExpiredAlert(true);
                        } else {
                            setError(`Microsoft authentication error: ${error.message}`);
                        }
                    }
                    finally {
                        setIsInitialized(true);
                        setLoading(false);
                    }
                }
                if (isGoogleLoggedIn === oAuthStatus.LOGGED_IN) {
                    try {
                        await initGoogleClient();
                        await new Promise(resolve => setTimeout(resolve, 500));
                        if (!window.gapi || !window.gapi.auth2) {
                            throw new Error("Google Auth not initialized properly");
                        }
                        const authInstance = window.gapi.auth2.getAuthInstance();
                        if (!authInstance) {
                            throw new Error("Failed to get Google Auth instance");
                        }
                        const googleUser = authInstance.currentUser.get();
                        if (googleUser && googleUser.isSignedIn()) {
                            const profile = googleUser.getBasicProfile();
                            const googleAccount = {
                                provider: AuthProvider.GOOGLE,
                                id: profile.getId(),
                                name: profile.getName(),
                                email: profile.getEmail(),
                                username: profile.getEmail(),
                                photoURL: profile.getImageUrl(),
                            };
                            setUser(googleAccount);
                            if (authManager) {
                                const storedGoogleAccounts = authManager.getAccounts(AuthProvider.GOOGLE);
                                let accountExists = false;
                                let existingAccount = null;
                                for (const acc of storedGoogleAccounts) {
                                    if (acc.userData && acc.userData.email === googleAccount.email) {
                                        accountExists = true;
                                        existingAccount = acc;
                                        break;
                                    }
                                }
                                if (!accountExists) {
                                    try {
                                        if (!canAddMoreAccounts()) {
                                            setShowUpgradePrompt(true);
                                            return;
                                        }
                                        const accountRecord = authManager.addAccount(AuthProvider.GOOGLE, googleAccount);
                                        if (accountRecord) {
                                            authManager.setActiveAccount(accountRecord.id);
                                            if (!activeAccount) {
                                                setActiveAccount(googleAccount);
                                            }
                                            if (!fetchingEmails && (!activeAccount || activeAccount.provider === AuthProvider.GOOGLE)) {
                                                setFetchingEmails(true);
                                                await fetchGmailEmails(googleAccount, setLoading, setEmails, setError);
                                                setFetchingEmails(false);
                                            }
                                        }
                                    } catch (error) {
                                        if (error.message && error.message.includes("Account limit reached")) {
                                            setShowUpgradePrompt(true);
                                            return;
                                        }
                                        throw error;
                                    }
                                } else if (existingAccount) {
                                    authManager.updateUserData(existingAccount.id, googleAccount);
                                    if (!activeAccount) {
                                        authManager.setActiveAccount(existingAccount.id);
                                        setActiveAccount(googleAccount);
                                        if (!fetchingEmails) {
                                            setFetchingEmails(true);
                                            await fetchGmailEmails(googleAccount, setLoading, setEmails, setError);
                                            setFetchingEmails(false);
                                        }
                                    }
                                }
                            }
                        } else {
                            await handleGoogleLogin(authManager, setUser, setLoading, setError, setIsGoogleLoggedIn);
                        }
                    } catch (error) {
                        if (error.message && error.message.includes("Account limit reached")) {
                            setShowUpgradePrompt(true);
                        } else {
                            setError(`Google account handling failed: ${error.message}`);
                            setIsGoogleLoggedIn(oAuthStatus.NOT_LOGGED_IN);
                        }
                    }
                }
            } catch (err) {
                setError(`Authentication initialization failed: ${err.message}`);
            } finally {
                setIsInitialized(true);
                setLoading(false);
            }
        };

        if (!isInitialized && !initializedRef.current) {
            initializeAuth();
        }
    }, [isGoogleLoggedIn, setIsGoogleLoggedIn, isMicrosoftLoggedIn, setError, setEmails, setLoading, setUser, authManager, activeAccount, canAddMoreAccounts, isInitialized, fetchingEmails]);

    const handleSessionExpired = async () => {
        if (!expiredAccountInfo) {
            setShowSessionExpiredAlert(false);
            return;
        }
        try {
            setLoading(true);
            const accountsToRemove = expiredAccountInfo.accounts || [];
            for (const account of accountsToRemove) {
                if (account.provider === AuthProvider.MICROSOFT) {
                    await handleLogout(account.id, setUser, setLoading, setError, setIsMicrosoftLoggedIn);
                } else if (account.provider === AuthProvider.GOOGLE) {
                    await handleGoogleLogout(authManager, account.id, setUser, setLoading, setError, setIsGoogleLoggedIn);
                }
            }
            setExpiredAccountInfo(null);
            setShowSessionExpiredAlert(false);
            setError("Your session has expired. Please log in again to continue.");
        } catch (error) {
            setError(`Failed to handle expired session: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleAccountSelect = async (account) => {
        try {
            if (!account || !account.provider) {
                throw new Error("Invalid account selected");
            }
            setLoading(true);
            setActiveAccount(account);
            if (authManager) {
                const accountId = account.id || `${account.provider}-${account.email || account.username}`;
                const storedAccount = authManager.getAccounts().find(acc => acc.id === accountId);

                if (storedAccount) {
                    authManager.setActiveAccount(storedAccount.id);
                }
            }
            const currentFolder = 'inbox';
            if (account.provider === AuthProvider.MICROSOFT) {
                await fetchEmails(account, setLoading, setEmails, setError, currentFolder);
            } else if (account.provider === AuthProvider.GOOGLE) {
                await fetchGmailEmails(account, setLoading, setEmails, setError, currentFolder);
            } else {
                throw new Error(`Unsupported provider: ${account.provider}`);
            }

            setShowAccountSelector(false);
        } catch (err) {
            setError(`Failed to load emails: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveAccount = async (account) => {
        try {
            setLoading(true);
            if (!authManager) {
                throw new Error("Auth manager is required");
            }
            const accountId = account.id || `${account.provider}-${account.email || account.username}`;
            const storedAccount = authManager.getAccounts().find(acc => acc.id === accountId);
            if (!storedAccount) {
                throw new Error("Account not found");
            }

            let result = false;
            if (account.provider === AuthProvider.MICROSOFT) {
                result = await handleLogout(accountId, setUser, setLoading, setError, setIsMicrosoftLoggedIn);
            } else if (account.provider === AuthProvider.GOOGLE) {
                result = await handleGoogleLogout(authManager, accountId, setUser, setLoading, setError, setIsGoogleLoggedIn);
            }

            if (result) {
                if (authManager.getAccounts().some(acc => acc.id === accountId)) {
                    authManager.removeAccount(accountId);
                }
                try {
                    const savedAccounts = localStorage.getItem('AuthAccounts');
                    if (savedAccounts) {
                        const parsedAccounts = JSON.parse(savedAccounts);
                        const updatedAccounts = parsedAccounts.filter(acc => acc.id !== accountId);
                        if (parsedAccounts.length !== updatedAccounts.length) {
                            localStorage.setItem('AuthAccounts', JSON.stringify(updatedAccounts));
                        }
                    }
                    const activeAccounts = localStorage.getItem('ActiveAuthAccounts');
                    if (activeAccounts) {
                        const parsedActiveAccounts = JSON.parse(activeAccounts);
                        if (parsedActiveAccounts[account.provider] === accountId) {
                            const remainingAccounts = authManager.getAccounts().filter(
                                acc => acc.provider === account.provider && acc.id !== accountId
                            );

                            if (remainingAccounts.length > 0) {
                                parsedActiveAccounts[account.provider] = remainingAccounts[0].id;
                            } else {
                                delete parsedActiveAccounts[account.provider];
                            }
                            localStorage.setItem('ActiveAuthAccounts', JSON.stringify(parsedActiveAccounts));
                        }
                    }
                    const tokens = localStorage.getItem('AuthTokens');
                    if (tokens) {
                        const parsedTokens = JSON.parse(tokens);
                        if (parsedTokens[accountId]) {
                            delete parsedTokens[accountId];
                            localStorage.setItem('AuthTokens', JSON.stringify(parsedTokens));
                        }
                    }
                } catch (storageError) { }
                if (activeAccount && ((activeAccount.username === account.username && activeAccount.provider === account.provider) || (activeAccount.userData && activeAccount.userData.username === account.username && activeAccount.userData.provider === account.provider))) {
                    const remainingAccounts = authManager.getLoggedInAccounts();
                    if (remainingAccounts.length > 0) {
                        setTimeout(() => {
                            handleAccountSelect(remainingAccounts[0]);
                        }, 0);
                    } else {
                        setActiveAccount(null);
                        setEmails([]);
                    }
                }
                const remainingProviderAccounts = authManager.getAccounts(account.provider);
                if (remainingProviderAccounts.length === 0) {
                    if (account.provider === AuthProvider.GOOGLE) {
                        setIsGoogleLoggedIn(oAuthStatus.NOT_LOGGED_IN);
                    } else if (account.provider === AuthProvider.MICROSOFT) {
                        setIsMicrosoftLoggedIn(oAuthStatus.NOT_LOGGED_IN);
                    }
                }
                authManager.notifyStateChange();
                setUpdateTrigger(prev => prev + 1);
                setAccounts(authManager.getLoggedInAccounts());
            }
        } catch (error) {
            setError(`Failed to remove account: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleAddAccount = useCallback((account) => {
        try {
            if (!authManager) {
                throw new Error("Auth manager is required");
            }
            if (!canAddMoreAccounts()) {
                setShowUpgradePrompt(true);
                return;
            }
            const accountRecord = authManager.addAccount(account.provider, account);
            if (accountRecord) {
                authManager.setActiveAccount(accountRecord.id);
                authManager.notifyStateChange();
                setUpdateTrigger(prev => prev + 1);
            }
        } catch (error) {
            if (error.message && error.message.includes("Account limit reached")) {
                setShowUpgradePrompt(true);
            } else {
                setError(`Failed to add account: ${error.message}`);
            }
        }
    }, [canAddMoreAccounts, setError, authManager]);

    if (showSessionExpiredAlert) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
                <div className={`w-96 rounded-lg shadow-xl ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'} border ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'} transition-all duration-300`}>
                    <div className="p-4 border-b flex justify-between items-center">
                        <h2 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Session Expired</h2>
                        <button onClick={() => setShowSessionExpiredAlert(false)} className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full p-2 transition-colors">
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                    <div className="p-6">
                        <div className="text-center mb-4">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-100 text-yellow-500 mb-4">
                                <i className="bi bi-exclamation-triangle-fill text-2xl"></i>
                            </div>
                            <h3 className={`text-lg font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Authentication Session Expired</h3>
                            <p className={`mt-2 ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-300'}`}>
                                {expiredAccountInfo?.provider === AuthProvider.MICROSOFT ?
                                    "Your Microsoft account session has expired. You need to log out and log in again to continue using Microsoft services." :
                                    "Your account session has expired. You need to log out and log in again to continue."}
                            </p>
                        </div>
                        <div className="flex justify-center space-x-3 mt-6">
                            <button onClick={() => setShowSessionExpiredAlert(false)} className={`px-4 py-2.5 rounded-lg font-medium transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'bg-gray-100 hover:bg-gray-200 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-white'}`}>Cancel</button>
                            <button onClick={handleSessionExpired} className={`px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors duration-200 shadow-sm hover:shadow`}>Log Out & Refresh</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`flex flex-col gap-4 justify-center items-center h-screen ${appMode === AppMode.LIGHT ? light_mode_primary : dark_mode_primary}`}>
                <div className={`${appMode === AppMode.LIGHT ? light_mode_secondary : dark_mode_secondary} p-8 rounded-lg shadow-lg max-w-md w-full`}>
                    <p className="font-medium text-center">{error}</p>
                </div>
                <div className="flex gap-3">
                    <button onClick={() => setError(null)} className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors font-medium">Dismiss</button>
                    <button onClick={() => window.location.reload()} className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors font-medium">Retry</button>
                </div>
            </div>
        );
    }

    if (!isInitialized || loading) {
        return <LoadingScreen appMode={appMode} />;
    }

    if (!user && (isGoogleLoggedIn === oAuthStatus.NOT_LOGGED_IN || isGoogleLoggedIn === oAuthStatus.NOT_DETERMINED) &&
        (isMicrosoftLoggedIn === oAuthStatus.NOT_LOGGED_IN || isMicrosoftLoggedIn === oAuthStatus.NOT_DETERMINED)) {
        return <NoAuthScreen appMode={appMode} setActiveTab={setActiveTab} />;
    }

    return (
        <>
            <VerticalTabs appMode={appMode} user={user} emails={emails} setError={setError} loading={loading} setLoading={setLoading} setUser={setUser} setEmails={setEmails} accounts={accounts} activeAccount={activeAccount} handleAddAccount={handleAddAccount} handleAccountSelect={handleAccountSelect} handleRemoveAccount={handleRemoveAccount} authManager={authManager} subscriptionTier={subscriptionTier} />
            {showAccountSelector && <AccountSelector appMode={appMode} accounts={accounts} onClose={() => setShowAccountSelector(false)} onSelectAccount={handleAccountSelect} onRemoveAccount={handleRemoveAccount} activeAccount={activeAccount} subscriptionInfo={subscriptionInfo} />}
            {showUpgradePrompt && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className={`w-96 rounded-lg shadow-xl ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'} border ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'} transition-all duration-300`}>
                        <div className="p-4 border-b flex justify-between items-center">
                            <h2 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Account Limit Reached</h2>
                            <button onClick={() => setShowUpgradePrompt(false)} className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full p-2 transition-colors">
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="text-center mb-4">
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-100 text-yellow-500 mb-4">
                                    <i className="bi bi-exclamation-triangle-fill text-2xl"></i>
                                </div>
                                <h3 className={`text-lg font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Subscription Limit Reached</h3>
                                <p className={`mt-2 ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-300'}`}>You've reached the maximum number of accounts for your current {subscriptionInfo?.name} plan.</p>
                            </div>
                            <div className="flex justify-center space-x-3 mt-6">
                                <button onClick={() => setShowUpgradePrompt(false)} className={`px-4 py-2.5 rounded-lg font-medium transition-colors duration-200 ${appMode === AppMode.LIGHT ? 'bg-gray-100 hover:bg-gray-200 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-white'}`}>Cancel</button>
                                <button onClick={handleUpgradeRedirect} className={`px-4 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium transition-colors duration-200 shadow-sm hover:shadow`}>Upgrade Now</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function VerticalTabs({ appMode, setLoading, emails, setEmails, setError, accounts, activeAccount, handleAccountSelect, subscriptionTier }) {
    const dropdownRef = useRef(null);
    const accountsDropdownRef = useRef(null);
    const [activeTab, setActiveTab] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedFolder, setSelectedFolder] = useState('inbox');
    const [showFolderDropdown, setShowFolderDropdown] = useState(false);
    const [showAccountsDropdown, setShowAccountsDropdown] = useState(false);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowFolderDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (accountsDropdownRef.current && !accountsDropdownRef.current.contains(event.target)) {
                setShowAccountsDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => { }, [selectedFolder]);

    const handleClick = (index) => setActiveTab(index);

    const filteredEmails = emails.filter(
        (message) =>
            message.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            message.message?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            message.subject?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const toggleAccountsDropdown = () => {
        setShowAccountsDropdown(!showAccountsDropdown);
    };

    const toggleFolderDropdown = () => {
        setShowFolderDropdown(!showFolderDropdown);
    };

    const handleFolderChange = async (folder) => {
        setSelectedFolder(folder);
        setShowFolderDropdown(false);
        setLoading(true);
        try {
            if (activeAccount) {
                const accountInfo = getAccountInfo(activeAccount);
                if (accountInfo.provider === AuthProvider.MICROSOFT) {
                    await fetchEmails(activeAccount, setLoading, setEmails, setError, folder);
                } else if (accountInfo.provider === AuthProvider.GOOGLE) {
                    await fetchGmailEmails(activeAccount, setLoading, setEmails, setError, folder);
                }
            }
        } catch (error) {
            setError(`Failed to load ${folder} emails: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const getFolderDisplayName = (folder) => {
        switch (folder) {
            case 'inbox': return 'Inbox';
            case 'junk': return 'Junk Email';
            case 'sent': return 'Sent Items';
            case 'drafts': return 'Drafts';
            case 'archive': return 'Archive';
            case 'trash': return 'Trash';
            default: return 'Inbox';
        }
    };

    const getFolderIcon = (folder) => {
        switch (folder) {
            case 'inbox': return 'inbox';
            case 'junk': return 'exclamation-triangle';
            case 'sent': return 'send';
            case 'drafts': return 'folder';
            case 'archive': return 'archive';
            case 'trash': return 'trash';
            default: return 'inbox';
        }
    };

    const getProviderIcon = (provider) => {
        return provider === AuthProvider.MICROSOFT ? 'bi bi-microsoft' : 'bi bi-google';
    };

    const getProviderName = (provider) => {
        return provider === AuthProvider.MICROSOFT ? 'Microsoft' : 'Google';
    };

    const getProviderColor = (provider) => {
        return provider === AuthProvider.MICROSOFT ? 'text-blue-500' : 'text-red-500';
    };

    const getAccountInfo = (account) => {
        if (!account) return { email: 'No email', provider: AuthProvider.GOOGLE };
        const userData = account?.userData || account || {};
        const email = userData.email ||
            userData.username ||
            userData.userPrincipalName ||
            'No email';
        const provider = account?.provider ||
            (userData.provider) ||
            (email.includes('gmail') ? AuthProvider.GOOGLE : AuthProvider.MICROSOFT);

        return { email, provider };
    };

    const isAccountActive = (account) => {
        if (!activeAccount) return false;
        const accountInfo = getAccountInfo(account);
        const activeInfo = getAccountInfo(activeAccount);

        return accountInfo.provider === activeInfo.provider &&
            accountInfo.email === activeInfo.email;
    };

    // const isFeatureEnabled = (minTier) => {
    //     return subscriptionTier >= minTier;
    // };

    return (
        <div className="flex h-screen">
            <div className={`${appMode === AppMode.LIGHT ? 'bg-white border-r border-gray-200' : dark_mode_primary + ' border-r border-gray-900'} w-1/4 h-full text-[#8E92BC] flex flex-col transition-all duration-500 ease-in-out shadow-sm`}>
                <div className={`py-6 px-5 flex-col ${appMode === AppMode.LIGHT ? 'border-b border-gray-100' : 'border-b border-gray-800'}`}>
                    <div className="mb-4 relative w-full">
                        <button onClick={toggleAccountsDropdown} className={`flex items-center gap-2 px-3 py-2.5 ${appMode === AppMode.LIGHT ? 'text-gray-800 bg-gray-100 hover:bg-gray-200' : dark_mode_secondary + ' hover:opacity-90'} rounded-lg transition-colors duration-200 w-full shadow-sm`}>
                            <div className={`h-8 w-8 rounded-full ${appMode === AppMode.LIGHT ? 'bg-gray-300' : 'bg-gray-700'} flex items-center justify-center overflow-hidden border ${appMode === AppMode.LIGHT ? 'border-white' : 'border-gray-600'}`}>
                                {activeAccount?.photoURL || (activeAccount?.userData && activeAccount.userData.photoURL)
                                    ? <img src={activeAccount.photoURL || activeAccount.userData.photoURL} alt="" className="h-full w-full object-cover" />
                                    : <i className={`${getProviderIcon(getAccountInfo(activeAccount).provider)} ${getProviderColor(getAccountInfo(activeAccount).provider)}`}></i>
                                }
                            </div>
                            <div className="text-left flex-grow">
                                <p className="text-sm font-medium truncate max-w-[120px]">{activeAccount?.userData?.name || activeAccount?.userData?.username || activeAccount?.userData?.email || activeAccount?.name || activeAccount?.username || activeAccount?.email || 'Account'}</p>
                                <p className="text-xs text-gray-500 flex items-center">
                                    <i className={`${getProviderIcon(getAccountInfo(activeAccount).provider)} mr-1 ${getProviderColor(getAccountInfo(activeAccount).provider)}`}></i> {getProviderName(getAccountInfo(activeAccount).provider)}
                                </p>
                            </div>
                            <i className="bi bi-chevron-down text-gray-500"></i>
                        </button>
                        {showAccountsDropdown && (
                            <div ref={accountsDropdownRef} className={`absolute left-0 top-full mt-1 ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200' : dark_mode_primary + ' border border-gray-900'} rounded-lg shadow-lg z-10 w-full py-2 transition-all duration-300`}>
                                <div className={`px-3 py-2 border-b ${appMode === AppMode.LIGHT ? 'border-gray-100' : 'border-gray-800'}`}>
                                    <p className="text-sm font-medium text-gray-500">Accounts</p>
                                </div>
                                {accounts.map((account, index) => {
                                    const userData = account.userData || account;
                                    const isActive = isAccountActive(account);
                                    const accountInfo = getAccountInfo(account);
                                    return (
                                        <div key={account.id || index} className={`px-3 py-2.5 flex items-center justify-between cursor-pointer ${appMode === AppMode.LIGHT ? 'hover:bg-gray-100' : 'hover:bg-gray-800'} ${isActive ? appMode === AppMode.LIGHT ? 'bg-blue-50' : dark_mode_secondary : ''} transition-colors duration-200`} onClick={() => { handleAccountSelect(account); setShowAccountsDropdown(false); }}>
                                            <div className="flex items-center gap-2">
                                                <div className={`h-8 w-8 rounded-full ${appMode === AppMode.LIGHT ? 'bg-gray-200' : 'bg-gray-600'} flex items-center justify-center overflow-hidden border-2 ${appMode === AppMode.LIGHT ? 'border-white' : 'border-gray-600'}`}>
                                                    {userData.photoURL
                                                        ? <img src={userData.photoURL} alt="" className="h-full w-full object-cover" />
                                                        : <i className={`${getProviderIcon(accountInfo.provider)} ${getProviderColor(accountInfo.provider)}`}></i>
                                                    }
                                                </div>
                                                <div>
                                                    <p className={`text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'} truncate max-w-[150px]`}>{userData.name || userData.username || userData.email}</p>
                                                    <p className="text-xs text-gray-500 flex items-center">
                                                        <i className={`${getProviderIcon(accountInfo.provider)} mr-1 ${getProviderColor(accountInfo.provider)}`}></i> {getProviderName(accountInfo.provider)}
                                                    </p>
                                                </div>
                                            </div>
                                            {isActive && <i className="bi bi-check-circle-fill text-blue-500"></i>}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <div className="relative">
                        <i className="bi bi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                        <input className={`w-full h-10 pl-10 pr-4 rounded-lg ${appMode === AppMode.LIGHT ? 'bg-gray-100' : dark_mode_primary} border ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'} focus:outline-none focus:ring-2 ${appMode === AppMode.LIGHT ? 'focus:ring-blue-200 focus:border-blue-500' : 'focus:ring-blue-800 focus:border-blue-700'} transition-all duration-200`} placeholder="Search emails" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                    </div>
                </div>
                <div className='flex-1 overflow-y-auto'>
                    <div className={`px-5 py-3 border-b ${appMode === AppMode.LIGHT ? 'border-gray-100' : 'border-gray-800'} flex justify-between items-center`}>
                        <div className="w-full relative">
                            <button onClick={toggleFolderDropdown} className={`w-full flex items-center justify-between gap-2 font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800 hover:text-gray-600' : 'text-white hover:text-gray-300'} transition-colors`} data-folder={selectedFolder}>
                                <div className="flex items-center">
                                    <i className={`bi bi-${getFolderIcon(selectedFolder)} mr-1`}></i> {getFolderDisplayName(selectedFolder)}
                                </div>
                                <i className="bi bi-chevron-down text-gray-500 text-xs"></i>
                            </button>
                            {showFolderDropdown && (
                                <div ref={dropdownRef} className={`absolute left-0 top-full mt-1 ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200' : dark_mode_primary + ' border border-gray-700'} rounded-t-none rounded-lg shadow-lg z-10 w-full py-1 transition-all duration-300`}>
                                    {['inbox', 'junk', 'sent', 'drafts', 'archive', 'trash'].map((folder) => (
                                        <div key={folder} className={`px-4 py-2 flex items-center cursor-pointer ${appMode === AppMode.LIGHT ? 'hover:bg-gray-100' : 'hover:bg-gray-800'} ${selectedFolder === folder ? appMode === AppMode.LIGHT ? 'bg-blue-50 text-blue-600' : 'bg-blue-900 text-blue-200' : appMode === AppMode.LIGHT ? 'text-gray-800' : ''} transition-colors duration-200`} onClick={() => handleFolderChange(folder)}>
                                            <i className={`bi bi-${getFolderIcon(folder)} mr-2`}></i>
                                            <span>{getFolderDisplayName(folder)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div></div>
                    </div>
                    {filteredEmails.length === 0
                        ? <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                            <i className="bi bi-inbox text-4xl mb-3"></i>
                            <p className="font-medium">No emails found</p>
                        </div>
                        : filteredEmails.map((email, index) => (
                            <React.Fragment key={email.id || index}>
                                <div className={`flex justify-between items-center py-4 px-5 cursor-pointer transition-colors duration-200 ${index === activeTab ? appMode === AppMode.LIGHT ? 'border-l-4 border-l-blue-500 bg-blue-50 hover:bg-blue-100' : dark_mode_secondary + ' border-l-4 border-l-blue-500' : appMode === AppMode.LIGHT ? 'hover:bg-gray-50' : 'hover:bg-gray-900'}`} onClick={() => handleClick(index)}>
                                    <div className="w-1/5 overflow-hidden flex-shrink-0">
                                        <img src={email.imageUrl} className={`h-10 w-10 rounded-full object-cover border ${appMode === AppMode.LIGHT ? "border-gray-200" : "border-gray-700"}`} alt='' />
                                    </div>
                                    <div className="w-4/5 flex-col flex-grow overflow-hidden">
                                        <div className="w-full flex justify-between items-center">
                                            <h1 className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'} truncate max-w-[130px] ${!email.read || index === activeTab ? 'font-semibold' : ''}`}>{email.name}</h1>
                                            <h2 className={`text-xs ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-200'}`}>{email.time}</h2>
                                        </div>
                                        <h2 className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-900' : 'text-gray-300'} font-medium truncate max-w-[180px] mt-1`}>{email.subject || email.message?.split('\n')[0] || "No Subject"}</h2>
                                        <p className='text-xs text-gray-500 truncate max-w-[180px] mt-1'>{email.message}</p>
                                    </div>
                                </div>
                                <hr className={`mx-4 ${appMode === AppMode.LIGHT ? 'border-gray-100' : 'border-gray-800'}`} />
                            </React.Fragment>
                        ))}
                </div>
            </div>
            <div className="flex-grow overflow-y-hidden">
                <EmailScreen appMode={appMode} setLoading={setLoading} selectedTab={activeTab} name={filteredEmails[activeTab]?.name} imageUrl={filteredEmails[activeTab]?.imageUrl} emails={filteredEmails} setEmails={setEmails} setError={setError} provider={getAccountInfo(activeAccount).provider} account={activeAccount} subscriptionTier={subscriptionTier} />
            </div>
        </div>
    );
}

function EmailScreen({ appMode, setLoading, selectedTab, name, imageUrl, emails, setEmails, setError, provider, account, subscriptionTier }) {
    const [reply, setReply] = useState(null);
    const [summary, setSummary] = useState(null);
    const [replyText, setReplyText] = useState('');
    const selectedEmail = emails && emails[selectedTab];
    const [showPopup1, setShowPopup1] = useState(false);
    const [showPopup2, setShowPopup2] = useState(false);//eslint-disable-next-line
    const [sendingReply, setSendingReply] = useState(false);
    const [popupLoading, setPopupLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteSuccess, setDeleteSuccess] = useState(false);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [showReplyPopup, setShowReplyPopup] = useState(false);//eslint-disable-next-line
    const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
    const [selectedAttachment, setSelectedAttachment] = useState(null);
    const [showAttachmentPreview, setShowAttachmentPreview] = useState(false);

    // const isFeatureEnabled = (minTier) => {
    //     return subscriptionTier >= minTier;
    // };

    const ActionButton = ({ icon, label, onClick, loading, active, requiredTier = 0 }) => {
        //const isEnabled = isFeatureEnabled(requiredTier);
        const handleClick = (e) => {
            e.preventDefault();
            setShowUpgradePrompt(true);
            // if (!isEnabled) {
            //     return;
            // }
            onClick();
        };

        return (
            <button className={`flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-colors duration-200 
                    ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-white'} 
                    ${active ? appMode === AppMode.LIGHT ? 'bg-blue-50 text-blue-600' : 'bg-blue-900 text-blue-200' : appMode === AppMode.LIGHT ? 'hover:bg-gray-100 hover:text-gray-800' : 'hover:bg-gray-800'} 
                    ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={handleClick} disabled={loading}
            >
                <i className={`bi bi-${icon} text-lg`}></i>
                <span className="text-xs mt-1 font-medium">{label}</span>
                {/*{!isEnabled && <span className="absolute top-0 right-0 h-2 w-2 bg-yellow-500 rounded-full"></span>}*/}
            </button>
        );
    };

    const handleAttachmentClick = async (attachment) => {
        try {
            setSelectedAttachment({ ...attachment, isLoading: true });
            setShowAttachmentPreview(true);
            let attachmentWithContent;
            if (provider === AuthProvider.MICROSOFT) {
                attachmentWithContent = await getMicrosoftAttachmentContent(
                    selectedEmail.account || account,
                    selectedEmail.fullEmail.id,
                    attachment.id,
                );
            } else {
                attachmentWithContent = await getGmailAttachmentContent(
                    selectedEmail.account || account,
                    selectedEmail.fullEmail.id,
                    attachment.id,
                );
            }
            setSelectedAttachment({
                ...attachment,
                ...attachmentWithContent,
                isLoading: false,
            });
        } catch (error) {
            setSelectedAttachment({
                ...attachment,
                isLoading: false,
                error: `Failed to load preview: ${error.message}`,
            });
        }
    };

    const handleReplySuggestion = async () => {
        // if (!isFeatureEnabled(1)) {
        //     setShowUpgradePrompt(true);
        //     return;
        // }
        setShowPopup2(true);
        if (!reply) {
            setPopupLoading(true);
            try {
                const response = await getEmailReply(selectedEmail.message || selectedEmail.body || '');
                setReply(response);
                setReplyText(response);
            } catch (error) {
                setError(`Failed to generate reply: ${error.message}`);
            } finally {
                setPopupLoading(false);
            }
        }
    };

    const handleEmailSummary = async () => {
        // if (!isFeatureEnabled(1)) {
        //     setShowUpgradePrompt(true);
        //     return;
        // }
        setShowPopup1(true);
        if (!summary) {
            setSummaryLoading(true);
            try {
                const response = await getEmailSummary(selectedEmail.message || selectedEmail.body || '');
                setSummary(response);
            } catch (error) {
                setError(`Failed to generate summary: ${error.message}`);
            } finally {
                setSummaryLoading(false);
            }
        }
    };

    const handleReplyButtonClick = () => {
        setShowReplyPopup(true);
    };

    const handleDeleteEmail = async () => {
        setDeleteSuccess(true);
    };

    const handleDeleteConfirm = async () => {
        setDeleteLoading(true);
        try {
            if (provider === AuthProvider.MICROSOFT) {
                await deleteEmail(selectedEmail.account || account, selectedEmail.fullEmail.id, setLoading, setEmails, setError);
            } else {
                await deleteGmailEmail(selectedEmail.fullEmail.id, selectedEmail.account || account, setLoading, setError);
            }
            const updatedEmails = emails.filter((email, index) => index !== selectedTab);
            setEmails(updatedEmails);
            setDeleteSuccess(false);
        } catch (error) {
            setError(`Failed to delete email: ${error.message}`);
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleSendReply = async () => {
        if (!replyText.trim()) {
            setError("Reply cannot be empty");
            return;
        }

        setSendingReply(true);
        try {
            if (provider === AuthProvider.MICROSOFT) {
                await sendMicrosoftReply(selectedEmail.account || account, selectedEmail.fullEmail.id, replyText, setLoading, setError);
            } else {
                await sendGmailReply(selectedEmail.account || account, selectedEmail.fullEmail.threadId, selectedEmail.fullEmail.id, replyText, setLoading, setError);
            }
            setShowReplyPopup(false);
            setReplyText('');
            const updatedEmails = [...emails];
            if (updatedEmails[selectedTab]) {
                updatedEmails[selectedTab] = {
                    ...updatedEmails[selectedTab],
                    read: true
                };
                setEmails(updatedEmails);
            }
        } catch (error) {
            setError(`Failed to send reply: ${error.message}`);
        } finally {
            setSendingReply(false);
        }
    };

    if (!selectedEmail) {
        return (
            <div className={`h-screen flex flex-col items-center justify-center ${appMode === AppMode.LIGHT ? 'bg-gray-50' : dark_mode_primary} text-gray-500`}>
                <i className="bi bi-envelope-paper-fill text-5xl mb-4"></i>
                <h2 className="text-xl font-semibold mb-2">No Email Selected</h2>
                <p className=''>Select an email to view it's details</p>
            </div>
        );
    }

    return (
        <div className={`h-screen flex flex-col overflow-y-auto ${appMode === AppMode.LIGHT ? 'bg-gray-50' : dark_mode_primary}`}>
            <div className={`p-4 ${appMode === AppMode.LIGHT ? 'bg-white border-b border-gray-200' : dark_mode_secondary + ' border-b border-gray-700'} w-full flex justify-between items-center shadow-sm`}>
                <div className={`flex items-center space-x-4 `}>
                    <div className={`h-10 w-10 rounded-full overflow-hidden border ${appMode === AppMode.LIGHT ? 'border-gray-100' : 'border-gray-600'}`}>
                        <img src={imageUrl} className="w-full h-full object-cover" alt='' />
                    </div>
                    <div>
                        <h2 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>{name}</h2>
                        <p className={`text-xs ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'}`}>{selectedEmail.time}</p>
                    </div>
                </div>
                <div className="flex gap-x-2">
                    <ActionButton icon="reply" label="Reply" onClick={handleReplyButtonClick} active={showReplyPopup} />
                    <ActionButton icon="archive" label="Archive" />
                    <ActionButton icon="trash" label="Delete" onClick={handleDeleteEmail} loading={deleteLoading} />
                    <ActionButton icon="lightbulb" label="Summarize" onClick={handleEmailSummary} active={showPopup1} />
                </div>
                {deleteSuccess && <DeletePopup appMode={appMode} title={'Email'} clickAction={handleDeleteConfirm} setDeleteSuccess={setDeleteSuccess} deleteLoading={deleteLoading} onCancelled={() => setDeleteSuccess(false)} />}
            </div>
            <div className={`flex-1 overflow-y-auto p-6 ${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_primary} m-4 rounded-lg shadow-sm border ${appMode === AppMode.LIGHT ? 'border-gray-100' : 'border-gray-800'}`}>
                <div className="max-w-3xl mx-auto">
                    <h1 className={`text-2xl font-bold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'} mb-4`}>{selectedEmail.subject || selectedEmail.message.split('\n')[0] || "No Subject"}</h1>
                    <div className={`flex items-center space-x-2 text-sm text-gray-500 mb-6 pb-6 border-b ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'}`}>
                        <span className="flex items-center gap-2">
                            <i className="bi bi-calendar"></i> {selectedEmail.date || selectedEmail.time}
                        </span>
                        <span>•</span>
                        <span className="flex items-center gap-2">
                            <i className="bi bi-person"></i> {selectedEmail.to || "To: You"}
                        </span>
                    </div>
                    <div className={`${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-white'} leading-relaxed overflow-y-auto max-h-screen`} dangerouslySetInnerHTML={{ __html: selectedEmail.body || selectedEmail.message }} />
                    {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                        <div className={`mt-8 pt-6 ${appMode === AppMode.LIGHT ? 'border-t border-gray-200' : 'border-t border-gray-700'}`}>
                            <h3 className={`font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-white'} mb-3 flex items-center`}>
                                <i className="bi bi-paperclip mr-2"></i> Attachments ({selectedEmail.attachments.length})
                            </h3>
                            <div className="flex flex-wrap gap-3">
                                {selectedEmail.attachments.map((attachment, index) => (
                                    <div key={index} className={`p-3 border ${appMode === AppMode.LIGHT ? 'border-gray-200 hover:bg-gray-50' : 'border-gray-700 hover:bg-gray-800'} rounded-lg flex items-center space-x-2 transition-colors duration-200 cursor-pointer shadow-sm hover:shadow`} onClick={() => handleAttachmentClick(attachment)}>
                                        <i className="bi bi-file-earmark-fill text-blue-500 text-lg"></i>
                                        <span className="text-sm font-medium">{attachment.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className={`p-4 ${appMode === AppMode.LIGHT ? 'bg-white border-t border-gray-200' : dark_mode_secondary + ' border-t border-gray-700'} shadow-sm`}>
                <div className={`max-w-3xl mx-auto`}>
                    <div className={`${appMode === AppMode.LIGHT ? 'bg-gray-50 border border-gray-200' : dark_mode_primary + ' border border-gray-700'} rounded-lg p-3 flex items-center shadow-sm hover:shadow transition-shadow duration-200`}>
                        <button onClick={handleReplyButtonClick} className="text-start w-full focus:outline-none px-3 py-2 bg-transparent">Type a quick reply...</button>
                        <button className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center gap-2 font-medium" onClick={handleReplyButtonClick}>
                            <i className="bi bi-send-fill"></i><span>Reply</span>
                        </button>
                    </div>
                </div>
                {showPopup1 && <ExplanationPopup appMode={appMode} explanation={summaryLoading ? null : summary} loading={summaryLoading} onClose={() => setShowPopup1(false)} title={'Summary'} setShowPopup={setShowPopup1} />}
                {showReplyPopup && <ReplyPopup appMode={appMode} reply={reply} replyText={replyText} setReplyText={setReplyText} setShowReplyPopup={setShowReplyPopup} selectedEmail={selectedEmail} loading={popupLoading} handleSendReply={handleSendReply} handleEmailReply={handleReplySuggestion} suggestionPopup={showPopup2} setSuggestionPopup={setShowPopup2} />}
                {showAttachmentPreview && selectedAttachment && (
                    <AttachmentErrorBoundary>
                        <AttachmentPreviewPopup appMode={appMode} attachment={selectedAttachment}
                            onClose={() => {
                                if (selectedAttachment.contentUrl &&
                                    !selectedAttachment.isDataUrl &&
                                    selectedAttachment.contentUrl.startsWith('blob:')) {
                                    URL.revokeObjectURL(selectedAttachment.contentUrl);
                                }
                                setShowAttachmentPreview(false);
                            }}
                        />
                    </AttachmentErrorBoundary>
                )}
            </div>
        </div>
    );
}